import { useParams } from "wouter";
import { Link } from "wouter";
import Header from "@/components/layout/header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { usePerk } from "@/hooks/usePerks";
import PerkWizard from "./wizard/PerkWizard";
import type { PerkWizardFormData } from "./wizard/PerkWizard";

export default function EditPerk() {
  const params = useParams<{ perkId: string }>();
  const perkId = params.perkId;
  const { data: perk, isLoading, error } = usePerk(perkId);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Edit Perk" />
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading perk</AlertTitle>
            <AlertDescription>Failed to load perk details for editing.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (isLoading || !perk) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Edit Perk" />
        <div className="flex-1 p-6 max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  // Map the enriched CmsPerkResponse to wizard form defaults.
  // All fields are now available from the API response.
  const defaultValues: Partial<PerkWizardFormData> = {
    title: perk.title || "",
    description: perk.description || "",
    vendorId: perk.vendorId || "",
    startDate: perk.startDate ? perk.startDate.split("T")[0] : "",
    endDate: perk.endDate ? perk.endDate.split("T")[0] : "",
    imageUrl: perk.imageUrl || "",
    isFeatured: perk.isFeatured || false,
    deliveryMethod: perk.deliveryMethod || "PromoCode",
    valueType: perk.valueType || "Percentage",
    discountValue: perk.discountValue ?? null,
    coinCost: perk.coinCost ?? 0,
    // Categories and locations from enriched response
    categoryIds: perk.categories?.map((c) => c.id).filter(Boolean) as string[] || [],
    locationIds: perk.locations?.map((l) => l.id).filter(Boolean) as string[] || [],
    // Delivery details
    promoCodePoolType: perk.promoCodePoolType || "Unique",
    sharedPromoCode: perk.sharedPromoCode || "",
    externalLinkUrl: perk.externalLinkUrl || "",
    redemptionInstructions: perk.redemptionInstructions || "",
    termsAndConditions: perk.termsAndConditions || "",
    // Value details
    currency: perk.currency || "AED",
    freeItemDescription: perk.freeItemDescription || "",
    // Caps and settings
    capFrequency: perk.capFrequency || "Once",
    maxRedemptionsPerEmployee: perk.maxRedemptionsPerEmployee ?? null,
    maxRedemptionsTotal: perk.maxRedemptionsTotal ?? null,
    soldOutBehavior: perk.soldOutBehavior || "ShowBadge",
    sortOrder: perk.sortOrder ?? 0,
  };

  return (
    <div className="flex flex-col h-full">
      <Header title={`Edit: ${perk.title}`} description="Update perk details">
        <Link href={`/perks/${perkId}`}>
          <Button variant="ghost">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Detail
          </Button>
        </Link>
      </Header>

      <div className="flex-1 overflow-auto p-6">
        <PerkWizard existingPerk={perk} defaultValues={defaultValues} />
      </div>
    </div>
  );
}
